import { CommonUtils, userStore } from "@/BaseComponent";

interface UploadResponse {
    url: string;
    fileName: string;
}

export default class CkEditorManager {
    private editorInstance: any = null;
    private elementId: string;
    private userToken: string;
    private apiUrl: string;
    private formState: { content: string };
    private uploadPath?: string;

    constructor(elementId: string, formState: { content: string }, uploadPath?: string) {
        const user = userStore();
        this.elementId = elementId;
        this.userToken = user.accessToken!;
        this.apiUrl = process.env.VUE_APP_API_URL;
        this.formState = formState;
        this.uploadPath = uploadPath;
    }

    async initEditor() {
        this.editorInstance = (window as any).CKEDITOR.replace(this.elementId, {
            toolbar: [
                { name: "document", items: ["Source"] },
                { name: "styles", items: ["Format", "FontSize"] },
                { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike", "RemoveFormat", "CopyFormatting"] },
                { name: "colors", items: ["TextColor", "BGColor"] },
                { name: "align", items: ["JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock"] },
                { name: "paragraph", items: ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "-", "Blockquote"] },
                { name: "insert", items: [this.uploadPath ? "Image" : "", "Table"] },
                { name: "links", items: ["Link", "Unlink"] },
                { name: "clipboard", items: ["Undo", "Redo"] },
            ],
            // extraAllowedContent: "h3{clear};h2{line-height};h2 h3{margin-left,margin-top}",
            extraAllowedContent: "a[*]{*}; h3{clear}; h2{line-height}; h2 h3{margin-left,margin-top};span(*){*}; p(*){*}",
            readOnly: false,
            extraPlugins: "uploadimage,clipboard,colordialog,colorbutton,font,justify",
            removePlugins: ["magicline"],
            uploadUrl: this.apiUrl + `/ckUpload/${this.uploadPath}`,
            filebrowserUploadUrl: this.apiUrl + `/ckUpload/${this.uploadPath}`,
            filebrowserBrowseUrl: this.apiUrl + `/ckUpload/${this.uploadPath}`,
            filebrowserImageBrowseUrl: this.apiUrl + `/ckUpload/${this.uploadPath}`,
            filebrowserImageUploadUrl: this.apiUrl + `/ckUpload/${this.uploadPath}`,
            autoGrow_minHeight: 220,
            autoGrow_maxHeight: 600,
            width: "100%",
            language: "ko",
            contentsCss: "ckeditor/contents.css",
            font_names: "나눔고딕/Nanum Gothic;고운돋움/바탕체/RIDIBatang;",
            format_tags: "p;h1;h2;h3;pre;div",
            stylesSet: [
                {
                    name: "Compact table",
                    element: "table",
                    attributes: { cellpadding: "5", cellspacing: "0", border: "1" },
                    styles: { "border-collapse": "collapse" },
                },
            ],
            clipboard_handleImages: false,
        });

        this.bindEvents();
    }

    private bindEvents() {
        // 업로드 요청 시 헤더 세팅
        this.editorInstance.on("fileUploadRequest", (evt: any) => {
            const xhr = evt.data.fileLoader.xhr;
            xhr.setRequestHeader("x-auth-token", `Bearer ${this.userToken}`);
            xhr.setRequestHeader("enctype", `multipart/form-data; charset=utf-8; boundary=${CommonUtils.makeBoundary()}`);
        });

        // 업로드 응답 가공
        this.editorInstance.on("fileUploadResponse", (evt: any) => {
            evt.stop();
            const data = evt.data,
                xhr = data.fileLoader.xhr,
                response = xhr.responseText.split("|");

            if (response[1]) {
                data.message = response[1];
                evt.cancel();
            } else {
                try {
                    const jsonObject: UploadResponse = JSON.parse(response[0]);
                    data.url = jsonObject.url + jsonObject.fileName;
                } catch (error) {
                    console.error("Upload response parse error:", error);
                    evt.cancel();
                }
            }
        });

        // 인스턴스 준비 후 기존 데이터 세팅
        this.editorInstance.on("instanceReady", () => {
            this.setDataInfo(this.formState.content);
        });

        this.editorInstance.on("blur", () => {
            this.editorInstance.updateElement();
        });

        this.editorInstance.on("mode", () => {
            if (this.editorInstance.mode === "source") {
                const editable = this.editorInstance.editable();
                editable.attachListener(editable, "keyup", () => {
                    this.editorInstance.updateElement();
                    this.formState.content = this.editorInstance.getData(); // ✅ Vue reactive에 반영
                });
            }
        });

        this.editorInstance.on("change", () => {
            const content = this.editorInstance.getData();
            this.formState.content = content; // ✅ Vue reactive state에 직접 반영
        });
    }

    async setDataInfo(content: string = "") {
        if (this.editorInstance) {
            await this.editorInstance.setData(CommonUtils.decodeContent(content));
        }
    }

    async getData(): Promise<string> {
        return (await this.editorInstance) ? this.editorInstance.getData() : "";
    }

    saveContent() {
        if (this.editorInstance) {
            this.editorInstance.updateElement(); // ✅ 소스 모드에서 form_state 반영
            this.formState.content = this.editorInstance.getData();
        }
    }

    destroy() {
        if (this.editorInstance) {
            this.editorInstance.destroy();
            this.editorInstance = null;

            if ((window as any).CKEDITOR?.instances) {
                Object.keys((window as any).CKEDITOR.instances).forEach((key) => {
                    (window as any).CKEDITOR.instances[key].destroy(true);
                    delete (window as any).CKEDITOR.instances[key];
                });
            }

            if ((window as any).CKEDITOR?.plugins?.magicline) {
                const doc = document;
                doc.removeEventListener("mousemove", (window as any).CKEDITOR.plugins.magicline.checkMouse, true);
            }
        }
    }
}
